import React from 'react';
import { resolveImagenUrl } from '../../config/apiConfig';

// ============================================================
// ColorInput - Selector de color reutilizable
// ============================================================
export function ColorInput({ label, value, onChange, tooltip }) {
    return (
        <div className="d-flex align-items-center gap-3 mb-3">
            <div style={{ position: 'relative', width: '44px', height: '44px', flexShrink: 0 }}>
                <input
                    type="color"
                    value={value}
                    onChange={onChange}
                    style={{
                        position: 'absolute', inset: 0, width: '100%', height: '100%',
                        padding: 0, border: '2px solid #e2e8f0', borderRadius: '10px',
                        cursor: 'pointer', opacity: 0, zIndex: 2
                    }}
                />
                <div style={{
                    width: '100%', height: '100%', borderRadius: '10px',
                    backgroundColor: value, border: '2px solid #e2e8f0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s'
                }}>
                    <span style={{ fontSize: '10px', color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.3)', opacity: 0.8 }}>🎨</span>
                </div>
            </div>
            <div className="flex-grow-1">
                <label className="form-label small fw-semibold text-secondary mb-0">{label}</label>
                <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-light text-dark border px-2 py-1" style={{ fontFamily: 'monospace', fontSize: '11px' }}>{value}</span>
                    {tooltip && <span className="text-muted" style={{ fontSize: '11px', cursor: 'help' }} title={tooltip}>ⓘ</span>}
                </div>
            </div>
        </div>
    );
}

// ============================================================
// TemaCard - Tarjeta de tema predefinido
// ============================================================
export function TemaCard({ tema, activo, onClick }) {
    return (
        <div
            className="card border-0 overflow-hidden"
            style={{
                cursor: 'pointer', transition: 'all 0.25s ease',
                borderRadius: '14px',
                boxShadow: activo ? '0 4px 20px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.06)',
                outline: activo ? '3px solid #008060' : 'none',
                outlineOffset: '2px',
                transform: activo ? 'scale(1.02)' : 'scale(1)',
            }}
            onClick={onClick}
        >
            <div style={{
                height: '70px',
                background: 'linear-gradient(135deg, ' + tema.colores.color_primario + ' 0%, ' + tema.colores.color_secundario + ' 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem',
                position: 'relative'
            }}>
                {tema.emoji}
                {activo && (
                    <div style={{
                        position: 'absolute', top: 6, right: 6,
                        backgroundColor: '#008060', color: '#fff',
                        borderRadius: '50%', width: '22px', height: '22px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '11px', fontWeight: 'bold'
                    }}>✓</div>
                )}
            </div>
            <div className="p-3" style={{ backgroundColor: tema.colores.color_fondo }}>
                <div className="d-flex gap-1 mb-2">
                    {[tema.colores.color_primario, tema.colores.color_secundario, tema.colores.color_btn_primario, tema.colores.btn_continuar_bg].map((c, i) => (
                        <span key={i} style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: c, display: 'inline-block', border: '1px solid rgba(0,0,0,0.1)' }}></span>
                    ))}
                </div>
                <h6 className="fw-bold mb-0" style={{ color: tema.colores.color_texto, fontSize: '0.85rem' }}>{tema.nombre}</h6>
                <small style={{ color: tema.colores.color_texto, opacity: 0.6 }}>{tema.descripcion}</small>
            </div>
        </div>
    );
}

// ============================================================
// BannerSelector - Selector de banners/imagen de portada
// ============================================================
export function BannerSelector({ banners, seleccionado, onSelect }) {
    return (
        <div className="d-flex flex-wrap gap-3">
            {banners.map((banner) => {
                const esSel = seleccionado === banner.url;
                return (
                    <div key={banner.id}
                        onClick={() => onSelect(banner.url)}
                        style={{
                            width: '150px', cursor: 'pointer', transition: 'all 0.2s',
                            border: esSel ? '3px solid #008060' : '1px solid #e2e8f0',
                            borderRadius: '14px', overflow: 'hidden',
                            boxShadow: esSel ? '0 4px 12px rgba(0,128,96,0.2)' : '0 1px 3px rgba(0,0,0,0.04)',
                            position: 'relative'
                        }}>
                        {esSel && (
                            <div style={{
                                position: 'absolute', top: 6, right: 6,
                                backgroundColor: '#008060', color: '#fff',
                                borderRadius: '50%', width: '22px', height: '22px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '11px', zIndex: 2, fontWeight: 'bold'
                            }}>✓</div>
                        )}
                        {banner.url ? (
                            <img src={banner.url} alt={banner.nombre} style={{ width: '100%', height: '70px', objectFit: 'cover' }} />
                        ) : (
                            <div style={{
                                width: '100%', height: '70px', backgroundColor: '#f8f9fa',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#adb5bd', fontSize: '13px'
                            }}>
                                <i className="bi bi-image me-1"></i> Sin foto
                            </div>
                        )}
                        <div className="p-2 text-center fw-semibold"
                            style={{
                                fontSize: '0.75rem',
                                backgroundColor: esSel ? '#e6f3f0' : '#fff',
                                color: esSel ? '#008060' : '#495057'
                            }}>
                            {banner.nombre}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ============================================================
// VistaPreviaMovil - Preview del celular
// ============================================================
export function VistaPreviaMovil({ config, estilos }) {
    const cq = estilos.carta_qr || {};

    // Resuelve clave R2 relativa → URL pública, o deja data: URLs intactas
    const logoUrl = config.urlLogo
        ? (config.urlLogo.startsWith('data:') || config.urlLogo.startsWith('http')
            ? config.urlLogo
            : resolveImagenUrl(config.urlLogo))
        : null;
    const bannerUrl = config.urlCabecera
        ? (config.urlCabecera.startsWith('data:') || config.urlCabecera.startsWith('http')
            ? config.urlCabecera
            : resolveImagenUrl(config.urlCabecera))
        : null;

    const headerBg = cq.header_bg || cq.color_primario || '#F26B21';
    const headerText = cq.header_text_color || '#FFFFFF';
    const footerBg = cq.footer_bg || '#1a1c1d';
    const footerText = cq.footer_text_color || '#FFFFFF';

    const logoSizes = { pequeño: 24, mediano: 32, grande: 42 };
    const logoSize = logoSizes[cq.logo_tamano] || 32;

    const cardRadii = { cuadrado: '4px', redondeado: '12px', extra: '20px' };
    const cardRadius = cardRadii[cq.tarjeta_radio] || '12px';
    const cardShadow = cq.tarjeta_sombra ? '0 2px 6px rgba(0,0,0,0.15)' : 'none';

    return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{
                width: '220px', height: '420px', borderRadius: '24px',
                border: '3px solid #1a1a1a', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                overflow: 'hidden', display: 'flex', flexDirection: 'column',
                position: 'relative', background: cq.color_fondo || '#f8f9fa'
            }}>
                {/* Notch */}
                <div style={{
                    position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
                    width: '60px', height: '14px', backgroundColor: '#1a1a1a',
                    borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px', zIndex: 10
                }} />

                {/* Header */}
                <div style={{
                    background: headerBg,
                    backgroundImage: bannerUrl ? `url(${bannerUrl})` : 'none',
                    backgroundSize: 'cover', backgroundPosition: 'center',
                    paddingTop: '18px', paddingBottom: '8px',
                    position: 'relative', flexShrink: 0
                }}>
                    {bannerUrl && (
                        <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.3)' }} />
                    )}
                    <div style={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
                        {/* Logo */}
                        <div style={{
                            width: `${logoSize + 6}px`, height: `${logoSize + 6}px`, borderRadius: '50%',
                            backgroundColor: '#fff', margin: '0 auto 4px', padding: '2px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden'
                        }}>
                            {logoUrl ? (
                                <img src={logoUrl} alt="logo"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                            ) : (
                                <span style={{ fontSize: '8px', color: '#aaa' }}>Logo</span>
                            )}
                        </div>
                        <div style={{ color: headerText, fontWeight: 700, fontSize: '10px' }}>
                            {config.nombre || 'Mi Tienda'}
                        </div>
                        {/* Buscador */}
                        <div style={{
                            margin: '5px 10px 4px', background: 'rgba(255,255,255,0.2)',
                            borderRadius: '20px', height: '13px'
                        }} />
                        {/* Chips de categorías */}
                        <div style={{ display: 'flex', gap: '3px', padding: '0 8px' }}>
                            <span style={{
                                background: headerText, color: headerBg,
                                borderRadius: '20px', padding: '1px 7px', fontSize: '6px', fontWeight: 700
                            }}>Todos</span>
                            <span style={{
                                background: 'rgba(255,255,255,0.2)', color: headerText,
                                borderRadius: '20px', padding: '1px 7px', fontSize: '6px'
                            }}>Bebidas</span>
                            <span style={{
                                background: 'rgba(255,255,255,0.2)', color: headerText,
                                borderRadius: '20px', padding: '1px 7px', fontSize: '6px'
                            }}>Platos</span>
                        </div>
                    </div>
                </div>

                {/* Grid de productos */}
                <div style={{ flex: 1, padding: '7px', overflowY: 'auto', background: cq.color_fondo || '#f8f9fa' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                        {[
                            { nombre: 'Hamburguesa', precio: '$5.990' },
                            { nombre: 'Completo', precio: '$3.500' },
                            { nombre: 'Papas Fritas', precio: '$2.990' },
                            { nombre: 'Bebida', precio: '$1.500' },
                        ].map((item, i) => (
                            <div key={i} style={{
                                background: '#fff', borderRadius: cardRadius,
                                boxShadow: cardShadow, overflow: 'hidden'
                            }}>
                                <div style={{ height: '36px', backgroundColor: '#e9ecef' }} />
                                <div style={{ padding: '3px 5px' }}>
                                    <div style={{ fontSize: '7px', fontWeight: 700, color: '#1a1c1d' }}>{item.nombre}</div>
                                    {cq.mostrar_precios !== false && (
                                        <div style={{ fontSize: '6px', color: headerBg, fontWeight: 600 }}>{item.precio}</div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer */}
                <div style={{
                    background: footerBg, color: footerText,
                    padding: '5px 8px', textAlign: 'center', flexShrink: 0
                }}>
                    <div style={{ fontSize: '7px', fontWeight: 600 }}>{config.nombre || 'Mi Tienda'}</div>
                    {config.direccion && (
                        <div style={{ fontSize: '6px', opacity: 0.7 }}>{config.direccion}</div>
                    )}
                    <div style={{ fontSize: '5px', opacity: 0.4, marginTop: '1px' }}>Powered by Komity</div>
                </div>
            </div>
        </div>
    );
}

// ============================================================
// SeccionCard - Card contenedor reutilizable
// ============================================================
export function SeccionCard({ icono, titulo, children, style }) {
    return (
        <div style={{
            backgroundColor: '#fff', borderRadius: '14px',
            border: '1px solid #e2e8f0', padding: '24px', marginBottom: '20px',
            ...style
        }}>
            <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                <i className={icono} style={{ color: '#008060' }}></i>
                {titulo}
            </h6>
            {children}
        </div>
    );
}
